import { Button, IconButton } from '@creditornot/cb-button';
import { Cross } from '@creditornot/cb-icons';
import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { RatingFilterValues } from 'apis/merchant-gateway/insights/reviews/types';
import { Typography as T } from 'common/typography';

import { RatingSmiley } from './Smiley';
import { telemetryClickFilterRatingsInInsightsV2RatingsReviews } from '../../telemetry';

const SmileysFilterRow = styled.div`
  display: flex;
  min-width: 356px;
  justify-content: space-between;
  padding: 24px 0;
`;

const Header = styled.div`
  display: flex;
  padding: 12px 0;
  justify-content: space-between;
  align-items: center;
  border-block-end: 1px solid ${cssThemedValue({ light: colors.pepper8, dark: colors.salt8 })};
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 16px;
`;
const RATING_VALUES = [1, 2, 3, 4, 5] as const;

const RatingSmileyStyled = styled(RatingSmiley)`
  width: 24px;
  height: 24px;
`;

const RatingSmileyStyledColored = styled(RatingSmileyStyled).attrs({ variant: 'colored' })``;
const RatingSmileyStyledOutline = styled(RatingSmileyStyled).attrs({ variant: 'outline' })``;

const FilterPopoverContent = ({
  close,
  filters,
  setFilters,
}: {
  close: () => void;
  filters: RatingFilterValues[];
  setFilters: (value: RatingFilterValues[]) => void;
}) => (
  <Root>
    <Header>
      <T.Body2 fontWeight="semiBold">
        <FormattedMessage id="insights.ratings-and-reviews.reviews-table-filter-title" />
      </T.Body2>
      <IconButton size="tiny" variant="grey" alignment="boundingBox" onClick={close}>
        <Cross />
      </IconButton>
    </Header>
    <SmileysFilterRow>
      {RATING_VALUES.map(rating => {
        const ratingStr = `rating_${rating}` as const;
        const isActive = filters.includes(ratingStr);

        return (
          <RatingButton
            active={isActive}
            onClick={() => {
              if (isActive) {
                setFilters(filters.filter(value => value !== ratingStr));
              } else {
                setFilters([...filters, ratingStr]);
              }
            }}
          >
            <RatingSmileyStyledColored rating={rating} />
            <RatingSmileyStyledOutline rating={rating} />
            <T.Subtitle>
              <FormattedMessage id={`insights.ratings-and-reviews.rating-level-name.${rating}`} />
            </T.Subtitle>
          </RatingButton>
        );
      })}
    </SmileysFilterRow>
  </Root>
);

export const RatingsFilterPopover = ({
  filters,
  setFilters,
}: {
  filters: RatingFilterValues[];
  setFilters: (value: RatingFilterValues[]) => void;
}) => (
  <Popover
    disableContentPadding
    showOnClick
    showOnHover={false}
    renderArrow={false}
    placement="bottom-end"
    content={({ close }) => <FilterPopoverContent close={close} filters={filters} setFilters={setFilters} />}
  >
    <Button size="small" variant="lightBlue" onClick={() => telemetryClickFilterRatingsInInsightsV2RatingsReviews()}>
      <FormattedMessage id="insights.ratings-and-reviews.table-filter-button" />
    </Button>
  </Popover>
);

const RatingButton = styled.button<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  min-width: 72px;

  border: none;
  background: none;
  padding: 8px;

  color: ${cssThemedValue('textSecondary')};

  & > ${RatingSmileyStyledColored} {
    display: none;
  }

  > ${RatingSmileyStyledOutline} {
    display: block;
  }

  &:hover {
    cursor: pointer;
    background-color: ${({ theme: { cbThemeMode = 'light' } }) =>
      cbThemeMode === 'light' ? colors.pepper8 : colors.salt8};
  }

  ${({ active }) => (active ? '&:not(:active)' : '&:active')} {
    color: ${cssThemedValue('textPrimary')};

    > ${RatingSmileyStyledColored} {
      display: block;
    }
    > ${RatingSmileyStyledOutline} {
      display: none;
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px ${colors.wolt24};
  }

  border-radius: 4px;
`;
